<script>
  import { BANK_IDS } from '@/store';
  import { GameLauncher } from '@/mixins/game-launcher';
  import { formatNumberToK } from '@/helpers/utils';
  import GamePicker from '@/components/form/GamePicker.vue';
  import IconCAF from '@/assets/images/svg/icons/shield-with-sword.svg';

  const STAKES = {
    MC: [100, 200, 500, 1000, 2000, 5000],
    QC: [100000, 200000, 500000, 1000000, 2000000, 5000000],
  };

  export default {
    name: 'CAFComponent',

    mixins: [GameLauncher],

    components: { GamePicker, IconCAF },

    data: () => ({
      selectedGame: null,
      selectedStake: 100,
    }),

    computed: {
      isMcMode() {
        return this.$store.state.playermode === 'MMC';
      },

      stakes() {
        return STAKES[this.isMcMode ? 'MC' : 'QC'];
      },
    },

    methods: {
      selectStake(stake) {
        this.selectedStake = stake;
      },

      getStakeLabel(stake) {
        const frontendStake = stake / 100;
        const currency = this.isMcMode ? '$' : 'QC ';
        return `${currency}${formatNumberToK(frontendStake)}`;
      },

      async launchGameIfReady() {
        if (this.$store.getters.getAcceptedFriendsList.length === 0 && !this.selectedGame.unityGame) {
          return await this.$store.dispatch('showAlertDialog', {
            title: 'Add friends',
            text: 'To continue, please first add friends.',
            permanent: true,
          });
        }

        if (this.selectedGame && this.selectedStake) {
          await this.openPrivateRoom();
        } else {
          await this.$store.dispatch('showAlertDialog', {
            title: 'Challenge not sent',
            text: 'To continue, please pick your game and room.',
          });
        }
      },

      async openPrivateRoom() {
        if (!this.checkUserBalance()) {
          return;
        }

        if (this.selectedGame.unityGame) {
          const buyIn = this.selectedStake;
          const hostId = this.$store.getters.getCurrentUserID;
          const addFriendUrl = process.env.VUE_APP_ADD_FRIEND_URL;

          try {
            await this.launchGameUnity(this.selectedGame.id, 'MLTP', addFriendUrl, 'extended', buyIn, true, hostId);
          } catch (error) {
            await this.$store.dispatch('showAlertDialog', {
              title: 'Challenge not sent',
              text: 'Error creating private room',
            });
          }
        } else {
          await this.$store.dispatch('requestGameplayTokenAs');
          let token = this.$store.getters.getGameplayToken;

          let selectedGameData = {
            gameId: this.selectedGame.id,
            bankId: this.isMcMode ? BANK_IDS.MMC : BANK_IDS.MQC,
            buyIn: this.selectedStake,
            privateRoomToken: token,
          };

          let returnData = await this.$store.dispatch('getPrivateRoomID', selectedGameData);

          if (returnData.privateRoomId) {
            await this.$store.dispatch('requestGameplayTokenAs');
            token = this.$store.getters.getGameplayToken;
            const addFriendUrl = process.env.VUE_APP_ADD_FRIEND_URL;

            let privateRoomData = {
              selectedFriends: [],
              token,
              privateRoomDbData: returnData.privateRoomDbData,
              addFriendUrl,
            };

            await this.$store.dispatch('issueChallenge', privateRoomData);

            const privateRoomURL = this.$store.getters.getPrivateRoomURL;

            if (privateRoomURL !== undefined) {
              await this.$store.dispatch('showIFrameDialog', {
                url: privateRoomURL,
                callback: () => {
                  this.closeModal();
                },
              });
            }
          } else {
            await this.$store.dispatch('showAlertDialog', {
              title: 'Challenge not sent',
              text: returnData.message || 'Error creating private room',
            });
          }
        }
      },

      closeModal() {
        this.$emit('update:selectedPriceIndex', 0);

        this.$store.dispatch('showConfirmDialog', {
          title: 'Exiting game',
          text: 'Are you sure you want to close the game?',
          callback: () => {
            this.$store.dispatch('hideIFrameDialog');
          },
        });
      },
    },
  };
</script>

<template>
  <div class="challenge-friends-card">
    <div class="mb-8 d-flex align-center caf-title">
      <IconCAF class="caf-icon"></IconCAF>
      <span class="gold--text font-size-32 font-weight-600 ml-2">Challenge Friends</span>
    </div>

    <div>
      <GamePicker v-model="selectedGame" />
    </div>

    <div class="selected-game" v-if="selectedGame">
      <img :src="selectedGame.img" alt="game thumbnail" />
      <div class="d-flex align-center list-wrapper">
        <ul>
          <li v-for="description in selectedGame.description" :key="description">
            {{ description }}
          </li>
        </ul>
      </div>
    </div>

    <div class="amount-section">
      <span class="buy-in-text">Choose Buy In Amount</span>
      <v-radio-group class="stakes" :value="selectedStake" @change="selectStake">
        <v-radio
          v-for="(stake, index) in stakes"
          :key="index"
          :label="getStakeLabel(stake)"
          :value="stake"
          color="black"
        />
      </v-radio-group>
    </div>

    <action-button>
      <template #action="{ loading, clickHandler }">
        <v-btn :loading="loading" class="create-room-btn w-full" @click="clickHandler(launchGameIfReady)">
          Create a Room
        </v-btn>
      </template>
    </action-button>
  </div>
</template>

<style scoped lang="scss">
  .challenge-friends-card {
    display: flex;
    flex-direction: column;
    margin: 0 auto;
    max-width: 510px;

    .selected-game {
      background: #24073d;
      border: 1px solid rgba(255, 255, 255, 0.1);
      border-radius: 10px;
      padding: 16px 10px;
      display: flex;
      gap: 8px;

      img {
        max-width: 120px;
        max-height: 120px;
        border-radius: 8px;

        @media screen and (min-width: 393px) {
          max-width: 140px;
          max-height: 140px;
        }

        @media screen and (min-width: 600px) {
          max-width: 140px;
          height: 140px;
        }
      }

      .list-wrapper {
        padding: 0;

        @media screen and (min-width: 393px) {
          min-width: 160px;
          padding: 10px;
        }
      }

      ul {
        list-style: disc;
      }

      li {
        color: #fff;
        font-size: 12px;
        margin-bottom: 6px;

        &:last-child {
          margin: 0;
        }
      }
    }

    .create-room-btn {
      background: #f8bf1e;
      height: 50px;
      padding: 12px 24px;
      font-size: 16px;
      text-transform: none;
      font-weight: 600;
      border-radius: 8px;
    }
  }

  .amount-section {
    margin-top: 16px;
    margin-bottom: 40px;

    .buy-in-text {
      color: white;
      font-size: 16px;
      font-weight: 500;
    }

    .stakes {
      &::v-deep {
        .v-input__slot {
          padding: 0 !important;
        }

        .v-input--radio-group__input {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 6px;

          .v-radio {
            flex-basis: 32%;
            padding: 8px 16px;
            border-radius: 20px;
            outline: 1px solid #756a81;
            height: 40px;

            label {
              color: white;
              text-wrap: nowrap;
            }

            &.v-item--active {
              background: #f8bf1e;
              outline: unset;

              label {
                font-weight: 600;
                color: #1c1c1c;
              }
            }
          }
        }

        .v-messages {
          display: none;
        }
      }
    }
  }

  .caf-icon {
    height: 26px;

    path {
      fill: var(--v-gold-base);
    }
  }
</style>
